import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { Input, InputProps } from '@antimatterhq/ui/components/input';
import { Text } from '@antimatterhq/ui/components/typography';

export type TypeaheadProps = {
  items: string[];
  placeholder?: string;
  minChars?: number;
  value?: string;
  'data-testid'?: string;
  filterResults?: boolean;
} & InputProps;

export const Typeahead = ({
  items,
  placeholder = 'Search...',
  minChars = 2,
  onBlur,
  value,
  name = '',
  onChange,
  'data-testid': dataTestId,
  filterResults = true,
  ...props
}: TypeaheadProps) => {
  const [showResults, setShowResults] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const blurTimeoutRef = useRef<NodeJS.Timeout>();

  const searchResults = useMemo(() => {
    if (!filterResults) return items;
    // Escape special regex characters in the input value
    const escapedValue = (value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const searchPattern = new RegExp(escapedValue, 'i');
    return items.filter((item) => searchPattern.test(item));
  }, [value, items, filterResults]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e);
      setShowResults(e.target.value.length >= minChars);
    },
    [onChange, minChars],
  );

  const handleOnSelect = (item: string) => {
    const syntheticEvent = {
      target: {
        value: item,
        name,
      },
    } as React.ChangeEvent<HTMLInputElement>;
    onChange?.(syntheticEvent);
    setShowResults(false);
  };

  const handleOnFocus = useCallback(() => {
    if ((value?.length || 0) < minChars) {
      return;
    }
    setShowResults(true);
  }, [value, minChars]);

  const handleOnBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      // Clear any existing timeout
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }

      // Set a new timeout to check if we're clicking on a dropdown item
      blurTimeoutRef.current = setTimeout(() => {
        const relatedTarget = e.relatedTarget as HTMLElement;
        const isClickingDropdown = relatedTarget?.closest('ul');

        if (!isClickingDropdown) {
          setShowResults(false);
          onBlur?.(e);
        }
      }, 200); // Increased timeout to ensure click events are processed
    },
    [onBlur],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div ref={wrapperRef} className="relative w-full">
      <Input
        type="text"
        value={value}
        onChange={handleInputChange}
        placeholder={placeholder}
        className="relative"
        onBlur={handleOnBlur}
        onFocus={handleOnFocus}
        name={name}
        data-testid={dataTestId}
        autoComplete="off"
        {...props}
      />

      {showResults && searchResults.length > 0 && (
        <ul className="absolute z-typeahead mt-2 max-h-60 w-full overflow-auto rounded-md border border-gray-300 bg-white shadow-lg dark:border-grey-700 dark:bg-grey-800">
          {searchResults.map((item, index) => (
            <li
              key={index}
              onMouseDown={() => handleOnSelect(item)}
              className="cursor-pointer px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700"
              // The test-id makes tests for dynamic options harder so we also have an id based on index
              id={`${dataTestId}-option-${index}`}
              data-testid={`${dataTestId}-option-${item}`}
            >
              <Text>{item}</Text>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
